// useWindowState.js
// Phase 6: Window division - UI state and simple handlers
// API handlers remain in ProjectDetail.js due to external dependencies

import { useState, useCallback } from 'react';
import { newLiteId } from '@/components/TypeImageMarkingSection';

export const useWindowState = () => {
  // ============================================
  // OPERATION LOADING STATE (prevents double-submit)
  // ============================================
  const [operationLoading, setOperationLoading] = useState(false);
  
  // ============================================
  // WINDOW DIALOG STATE
  // ============================================
  const [showWindowDialog, setShowWindowDialog] = useState(false);
  const [editingWindow, setEditingWindow] = useState(null);
  
  // ============================================
  // SEARCH, FILTER, SORT STATE
  // ============================================
  const [windowSearch, setWindowSearch] = useState('');
  const [windowFilters, setWindowFilters] = useState({ type: '', location: '', label: '' });
  const [windowSortBy, setWindowSortBy] = useState('createdAt_desc');
  
  // ============================================
  // SELECTION STATE
  // ============================================
  const [selectedWindowTypeIds, setSelectedWindowTypeIds] = useState([]);
  const [selectedWindowGlassTypeIds, setSelectedWindowGlassTypeIds] = useState([]);
  const [selectedWindowMetalPanelTypeIds, setSelectedWindowMetalPanelTypeIds] = useState([]);
  const [selectedWindowListIds, setSelectedWindowListIds] = useState([]);
  const [selectedWindowElevationIds, setSelectedWindowElevationIds] = useState([]);
  const [expandedWindowGlassTypeIds, setExpandedWindowGlassTypeIds] = useState([]);
  
  // ============================================
  // WINDOW METAL PANEL TYPE STATE
  // ============================================
  const [showWindowMetalPanelTypeDialog, setShowWindowMetalPanelTypeDialog] = useState(false);
  const [editingWindowMetalPanelType, setEditingWindowMetalPanelType] = useState(null);
  const [newWindowMetalPanelType, setNewWindowMetalPanelType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    thickness: '',
    colorCode: ''
  });
  const [showDeleteWindowMetalPanelTypeModal, setShowDeleteWindowMetalPanelTypeModal] = useState(false);
  const [deleteWindowMetalPanelTypeId, setDeleteWindowMetalPanelTypeId] = useState(null);
  
  // ============================================
  // ELEVATION STATE
  // ============================================
  const [selectedWindowElevation, setSelectedWindowElevation] = useState(null);
  const [selectedWindowForMarker, setSelectedWindowForMarker] = useState('');
  
  // ============================================
  // DELETE DIALOG STATE
  // ============================================
  const [showDeleteWindowTypeDialog, setShowDeleteWindowTypeDialog] = useState(false);
  const [windowTypeToDelete, setWindowTypeToDelete] = useState(null);
  const [showDeleteWindowDialog, setShowDeleteWindowDialog] = useState(false);
  const [windowToDelete, setWindowToDelete] = useState(null);
  const [showDeleteAllWindowsDialog, setShowDeleteAllWindowsDialog] = useState(false);
  
  // ============================================
  // WINDOW DETAIL DIALOG STATE
  // ============================================
  const [showWindowDetailDialog, setShowWindowDetailDialog] = useState(false);
  const [selectedWindowDetail, setSelectedWindowDetail] = useState(null);
  
  // ============================================
  // PICTURE UPLOAD STATE
  // ============================================
  const [pendingWindowTypePicture, setPendingWindowTypePicture] = useState(null);
  const [windowTypePictureUploading, setWindowTypePictureUploading] = useState(null);
  
  // ============================================
  // DRAWING VIEW STATE
  // ============================================
  const [viewingDrawing, setViewingDrawing] = useState(null);
  
  // ============================================
  // NEW WINDOW FORM STATE
  // ============================================
  const [newWindow, setNewWindow] = useState({
    windowTypeId: '',
    label: '',
    location: '',
    notes: '',
    glassTypeId: '',
    windowWidth_in: null,
    windowHeight_in: null,
    roWidth_in: null,
    roHeight_in: null,
    lites: [],
    liteMarks: [],
    liteMarksTypeId: null,
    hasGrid: false,
    gridSize_in: null
  });

  // ============================================
  // SIMPLE HANDLERS (No API calls)
  // ============================================

  // Confirm delete window type - opens dialog
  const confirmDeleteWindowType = useCallback((type, e) => {
    if (e) e.stopPropagation();
    setWindowTypeToDelete(type);
    setShowDeleteWindowTypeDialog(true);
  }, []);

  // Confirm delete window - opens dialog
  const confirmDeleteWindow = useCallback((window, e) => {
    if (e) e.stopPropagation();
    setWindowToDelete(window);
    setShowDeleteWindowDialog(true);
  }, []);

  // Edit window - populates form
  const handleEditWindow = useCallback((window) => {
    // Lites need stable ids so saved marks can reference them
    const lites = (window.lites || []).map(lite => ({ ...lite, id: lite.id || newLiteId() }));
    const liteIds = new Set(lites.map(l => l.id));
    // Marks only apply to the Window Type they were placed on
    const marksMatchType = !window.liteMarksTypeId || window.liteMarksTypeId === window.windowTypeId;
    const liteMarks = marksMatchType
      ? (window.liteMarks || []).filter(mark => liteIds.has(mark.liteId))
      : [];
    setEditingWindow(window);
    setNewWindow({
      windowTypeId: window.windowTypeId || '',
      label: window.label || '',
      location: window.location || '',
      notes: window.notes || '',
      glassTypeId: window.glassTypeId || '',
      windowWidth_in: window.windowWidth_in || null,
      windowHeight_in: window.windowHeight_in || null,
      roWidth_in: window.roWidth_in || null,
      roHeight_in: window.roHeight_in || null,
      lites: lites,
      liteMarks: liteMarks,
      liteMarksTypeId: liteMarks.length > 0 ? window.windowTypeId : null,
      hasGrid: window.hasGrid || false,
      gridSize_in: window.gridSize_in || null
    });
    setShowWindowDialog(true);
  }, []);

  // Close window dialog
  const handleCloseWindowDialog = useCallback((open) => {
    setShowWindowDialog(open);
    if (!open) {
      setEditingWindow(null);
      setNewWindow({
        windowTypeId: '',
        label: '',
        location: '',
        notes: '',
        glassTypeId: '',
        windowWidth_in: null,
        windowHeight_in: null,
        roWidth_in: null,
        roHeight_in: null,
        lites: [],
    liteMarks: [],
    liteMarksTypeId: null,
        hasGrid: false,
        gridSize_in: null
      });
    }
  }, []);

  // Clear all window selections
  const clearWindowSelections = useCallback(() => {
    setSelectedWindowTypeIds([]);
    setSelectedWindowGlassTypeIds([]);
    setSelectedWindowMetalPanelTypeIds([]);
    setSelectedWindowListIds([]);
    setSelectedWindowElevationIds([]);
  }, []);

  // Reset new window form
  const resetNewWindow = useCallback(() => {
    setNewWindow({
      windowTypeId: '',
      label: '',
      location: '',
      notes: '',
      glassTypeId: '',
      windowWidth_in: null,
      windowHeight_in: null,
      roWidth_in: null,
      roHeight_in: null,
      lites: [],
    liteMarks: [],
    liteMarksTypeId: null,
      hasGrid: false,
      gridSize_in: null
    });
  }, []);

  // View window detail
  const handleViewWindowDetail = useCallback((window) => {
    setSelectedWindowDetail(window);
    setShowWindowDetailDialog(true);
  }, []);

  // Close delete dialogs
  const closeDeleteWindowTypeDialog = useCallback(() => {
    setShowDeleteWindowTypeDialog(false);
    setWindowTypeToDelete(null);
  }, []);

  const closeDeleteWindowDialog = useCallback(() => {
    setShowDeleteWindowDialog(false);
    setWindowToDelete(null);
  }, []);

  // ============================================
  // RETURN ALL STATE AND HANDLERS
  // ============================================
  return {
    // Operation loading (prevents double-submit)
    operationLoading,
    setOperationLoading,
    
    // Dialog state
    showWindowDialog,
    setShowWindowDialog,
    editingWindow,
    setEditingWindow,
    
    // Search, filter, sort
    windowSearch,
    setWindowSearch,
    windowFilters,
    setWindowFilters,
    windowSortBy,
    setWindowSortBy,
    
    // Selection state
    selectedWindowTypeIds,
    setSelectedWindowTypeIds,
    selectedWindowGlassTypeIds,
    setSelectedWindowGlassTypeIds,
    selectedWindowMetalPanelTypeIds,
    setSelectedWindowMetalPanelTypeIds,
    selectedWindowListIds,
    setSelectedWindowListIds,
    selectedWindowElevationIds,
    setSelectedWindowElevationIds,
    expandedWindowGlassTypeIds,
    setExpandedWindowGlassTypeIds,
    
    // Window Metal Panel Type state
    showWindowMetalPanelTypeDialog,
    setShowWindowMetalPanelTypeDialog,
    editingWindowMetalPanelType,
    setEditingWindowMetalPanelType,
    newWindowMetalPanelType,
    setNewWindowMetalPanelType,
    showDeleteWindowMetalPanelTypeModal,
    setShowDeleteWindowMetalPanelTypeModal,
    deleteWindowMetalPanelTypeId,
    setDeleteWindowMetalPanelTypeId,
    
    // Elevation state
    selectedWindowElevation,
    setSelectedWindowElevation,
    selectedWindowForMarker,
    setSelectedWindowForMarker,
    
    // Delete dialog state
    showDeleteWindowTypeDialog,
    setShowDeleteWindowTypeDialog,
    windowTypeToDelete,
    setWindowTypeToDelete,
    showDeleteWindowDialog,
    setShowDeleteWindowDialog,
    windowToDelete,
    setWindowToDelete,
    showDeleteAllWindowsDialog,
    setShowDeleteAllWindowsDialog,
    
    // Window detail dialog
    showWindowDetailDialog,
    setShowWindowDetailDialog,
    selectedWindowDetail,
    setSelectedWindowDetail,
    
    // Picture upload
    pendingWindowTypePicture,
    setPendingWindowTypePicture,
    windowTypePictureUploading,
    setWindowTypePictureUploading,
    
    // Drawing view
    viewingDrawing,
    setViewingDrawing,
    
    // New window form
    newWindow,
    setNewWindow,
    resetNewWindow,
    
    // Simple handlers
    confirmDeleteWindowType,
    confirmDeleteWindow,
    handleEditWindow,
    handleCloseWindowDialog,
    clearWindowSelections,
    handleViewWindowDetail,
    closeDeleteWindowTypeDialog,
    closeDeleteWindowDialog
  };
};

export default useWindowState;
